/**
 * Productos exentos de un impuesto: la harina sin IVA.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ ESTA CUENTA ESTÁ ESCRITA DOS VECES, Y TIENEN QUE DAR LO MISMO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Aquí y en `backend/app/modules/catalog/exentos.py`. El cajero ve el total
 * antes de mandar nada; si las dos cuentas no coinciden, la venta se rechaza
 * con «sobran 0,90» delante del cliente. Mismos pasos, mismos redondeos.
 *
 * La regla: lo exento se SACA DE LA BASE de ese impuesto, y de ningún otro.
 *
 *   · IVA (alcanza a la venta entera): base = el total menos lo exento.
 *   · IGTF (grava lo que se paga en ciertas monedas): base = lo que entró en
 *     esas monedas, menos la parte de lo exento, en proporción.
 *
 * Por eso la harina exenta de IVA sigue pagando IGTF si se paga en dólares: el
 * IGTF grava el pago, no la mercancía, y la harina no está exenta de él.
 */

/**
 * A las 4 decimales con las que guarda el servidor. Es `redondearMonto` del
 * carrito, repetido aquí a propósito: el carrito usa este archivo, y traerla de
 * allá haría que cada uno necesitara al otro para cargar.
 */
function redondearMonto(n: number): number {
  return Math.round(n * 10000) / 10000;
}

/**
 * Cuánto de la venta está exento de cada impuesto.
 *
 * Recibe, por línea, lo que paga el cliente por ella y de qué está exenta.
 */
export function exentoPorImpuesto(
  lineas: { total: number; exentoDe?: string[] | null }[],
): Record<string, number> {
  const exento: Record<string, number> = {};
  for (const l of lineas) {
    for (const id of l.exentoDe ?? []) {
      exento[id] = (exento[id] ?? 0) + l.total;
    }
  }
  for (const [id, monto] of Object.entries(exento)) exento[id] = redondearMonto(monto);
  return exento;
}

/**
 * La base de un impuesto, quitándole lo que está exento de él.
 *
 * `sumaDeLineas` es lo que suman las líneas ANTES del descuento global. Con un
 * descuento a toda la compra la harina también se abarató, así que se quita su
 * parte en proporción: de 100 con un 10 % de descuento y 30 de harina, el IVA
 * va sobre 63 — no sobre 60, ni sobre 70.
 */
export function sinLoExento(base: number, exento: number, sumaDeLineas: number): number {
  if (exento <= 0 || base <= 0 || sumaDeLineas <= 0) return base;
  if (exento >= sumaDeLineas) return 0;
  // Sin descuentos es la resta pelada, sin dividir: así no se cuela ni una
  // diezmilésima de redondeo en el caso de todos los días.
  const parte =
    Math.abs(base - sumaDeLineas) < 0.00005 ? exento : redondearMonto((base * exento) / sumaDeLineas);
  return redondearMonto(Math.max(base - parte, 0));
}

/**
 * Si una línea de un ticket o de un presupuesto lleva la marca «(E)».
 *
 * Solo si está exenta de alguno de los impuestos que DE VERDAD se cobraron en
 * esa venta. Con el IVA apagado, marcar la harina «(E)» no explica nada: solo
 * hace preguntar por qué.
 */
export function llevaMarcaDeExento(
  exentoDe: string[] | null | undefined,
  cobrados: (string | null)[],
): boolean {
  if (!exentoDe || exentoDe.length === 0) return false;
  return exentoDe.some((id) => cobrados.includes(id));
}

/**
 * De qué impuestos cobrados hay algo exento en este papel: `["IVA"]`.
 *
 * Es lo que va en la leyenda «(E) Exento de IVA», al pie de la lista. Vacío =
 * no hay ninguna «(E)» en el papel, y entonces no sale la leyenda.
 */
export function nombresDeLosExentos(
  lineas: { exento_de?: string[] | null }[],
  cobrados: { tax_id: string | null; name: string }[],
): string[] {
  return cobrados
    .filter((t) => t.tax_id && lineas.some((l) => (l.exento_de ?? []).includes(t.tax_id as string)))
    .map((t) => t.name);
}

/**
 * Lo que cuesta una línea con el IVA encima, o `null` si no paga ninguno.
 *
 * El dueño lo pidió para el ticket: «que el precio de un producto se muestre
 * antes y después del IVA». Solo cuentan los impuestos que van sobre toda la
 * venta (el IVA) y que se cobraron en ella; el IGTF no, que va sobre el pago.
 * Lo exento, sin IVA.
 */
export function conImpuestosDeLaVenta(
  totalDeLinea: number,
  exentoDe: string[] | null | undefined,
  cobrados: { tax_id: string | null; rate: string; sobre_la_venta?: boolean }[],
): number | null {
  const tasa = cobrados
    .filter((t) => t.sobre_la_venta !== false && !(exentoDe ?? []).includes(t.tax_id ?? ""))
    .reduce((suma, t) => suma + (Number(t.rate) || 0), 0);
  if (tasa <= 0) return null;
  return redondearMonto(totalDeLinea * (1 + tasa));
}
